import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { Variants } from 'motion/react'
import type { HeroScene } from '@/lib/hero-scene'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { Moon, Sun } from 'lucide-react'
import { AnimatePresence, motion, useScroll, useTransform } from 'motion/react'
import { cn } from '@/lib/utils'
import { announceHeroScene } from '@/lib/hero-scene'
import SplitReveal from '@/components/generals/motion/split-reveal'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'

const EASE = [0.22, 1, 0.36, 1] as const
const SINK = [0.55, 0, 0.75, 0.2] as const

interface SceneDetails {
    label: string
    caption: string
    description: string
    image: string
    icon: LucideIcon
}

const SCENES: Record<HeroScene, SceneDetails> = {
    morning: {
        label: 'Morning',
        caption: 'Panels charge the battery',
        description: "Gridora forecasts every sunrise across your portfolio and stores the surplus instead of selling it at the day's cheapest hour.",
        image: '/images/hero-house-morning.jpg',
        icon: Sun
    },
    night: {
        label: 'Night',
        caption: 'Battery covers the peak',
        description: 'When the sun sets and prices climb, Gridora releases what you stored, so the evening peak runs on your own clean power.',
        image: '/images/hero-house-night.jpg',
        icon: Moon
    }
}

const ORDER: HeroScene[] = ['morning', 'night']

// Fixed positions so the server and the browser draw the same sky.
const STARS = Array.from({ length: 28 }, (_, index) => ({
    left: (index * 37.3 + 7) % 100,
    top: (index * 17.9 + 3) % 44,
    size: index % 4 === 0 ? 3 : index % 3 === 0 ? 2 : 1.5,
    delay: (index * 0.61) % 3.6
}))

// The hero is pinned while the visitor scrolls: the scene folds into a framed window,
// the copy lifts away, and a single line takes its place. Picking Morning or Night
// sinks the current house, raises the other one from below and relights the sky.
const HeroSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const switched = useRef(false)
    const still = usePrefersReducedMotion()
    const [scene, setScene] = useState<HeroScene>('morning')
    const night = scene === 'night'
    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end end'] })

    const frame = useTransform(
        scrollYProgress,
        [0, 1],
        ['inset(0% 0% 0% 0% round 0px)', 'inset(11% 5% 11% 5% round 44px)']
    )
    const sceneScale = useTransform(scrollYProgress, [0, 1], [1, 1.14])
    const titleY = useTransform(scrollYProgress, [0, 0.6], [0, -140])
    const controlsY = useTransform(scrollYProgress, [0, 0.6], [0, 120])
    const contentOpacity = useTransform(scrollYProgress, [0, 0.45], [1, 0])
    const lineOpacity = useTransform(scrollYProgress, [0.55, 0.85], [0, 1])
    const lineScale = useTransform(scrollYProgress, [0.55, 1], [0.92, 1])
    const lineBlur = useTransform(scrollYProgress, [0.55, 0.85], ['blur(10px)', 'blur(0px)'])

    useEffect(() => {
        announceHeroScene(scene)
    }, [scene])

    useEffect(() => () => announceHeroScene(null), [])

    const choose = (next: HeroScene): void => {
        switched.current = true
        setScene(next)
    }

    // The outgoing house is gone before the incoming one starts rising, so two houses
    // are never half visible on top of each other.
    const riseDelay = switched.current ? 0.42 : 0.9

    const house: Variants = {
        below: { y: '42%', opacity: 0, scale: 0.96 },
        shown: {
            y: '0%',
            opacity: 1,
            scale: 1,
            transition: {
                y: { duration: 1.4, ease: EASE, delay: riseDelay },
                scale: { duration: 1.4, ease: EASE, delay: riseDelay },
                opacity: { duration: 0.55, ease: 'easeOut', delay: riseDelay }
            }
        },
        hidden: {
            y: '30%',
            opacity: 0,
            scale: 0.97,
            transition: {
                y: { duration: 0.6, ease: SINK },
                scale: { duration: 0.6, ease: SINK },
                opacity: { duration: 0.32, ease: 'easeIn' }
            }
        }
    }

    return (
        <section
            ref={sectionRef}
            id='hero'
            aria-labelledby='hero-title'
            className={still ? 'relative h-svh bg-background' : 'relative h-[175svh] bg-background'}
        >
            <div className='sticky top-0 h-[100svh] overflow-hidden'>
                <motion.div aria-hidden='true' className='absolute inset-0 overflow-hidden' style={still ? undefined : { clipPath: frame }}>
                    <motion.div className='absolute inset-0' style={still ? undefined : { scale: sceneScale }}>
                        <div className={cn('absolute inset-0 transition-colors duration-[1400ms]', night ? 'bg-[#1b232c]' : 'bg-[#dcdccb]')} />

                        {/* The morning sky never fades, the night sky fades over it, so the
                            switch never dips through a half-transparent, washed-out frame. */}
                        {ORDER.map((key) => (
                            <div
                                key={`sky-${key}`}
                                className={cn(
                                    'absolute inset-0 transform-gpu will-change-[opacity]',
                                    key === 'night' && 'transition-opacity duration-[1400ms] ease-in-out',
                                    key === 'night' && !night ? 'opacity-0' : 'opacity-100'
                                )}
                            >
                                <Image
                                    src={SCENES[key].image}
                                    alt=''
                                    fill
                                    loading='eager'
                                    quality={40}
                                    sizes='50vw'
                                    className='scale-125 object-cover object-top blur-2xl'
                                />
                            </div>
                        ))}

                        <div
                            className={cn(
                                'absolute -right-[12%] -top-[24%] size-[70vmax] rounded-full bg-[radial-gradient(closest-side,rgba(255,221,180,0.55),rgba(255,221,180,0)_70%)] transition-opacity duration-[1400ms]',
                                night ? 'opacity-0' : 'opacity-100'
                            )}
                        />

                        <div className={cn('absolute inset-0 transition-opacity duration-[1600ms]', night ? 'opacity-100' : 'opacity-0')}>
                            <div className='absolute right-[6%] top-[4%] size-[34vmax] rounded-full bg-[radial-gradient(closest-side,rgba(186,204,232,0.22),rgba(186,204,232,0)_70%)]' />
                            <div className='absolute right-[17%] top-[17%] size-8 rounded-full bg-[#eef1f6] opacity-85 shadow-[0_0_48px_14px_rgba(206,218,240,0.28)] sm:size-11' />

                            {STARS.map((star, index) => (
                                <span
                                    key={index}
                                    className='hero-star absolute rounded-full bg-white'
                                    style={{
                                        left: `${star.left}%`,
                                        top: `${star.top}%`,
                                        width: star.size,
                                        height: star.size,
                                        animationDelay: `${star.delay}s`
                                    }}
                                />
                            ))}
                        </div>

                        <div className='hero-fog-slow absolute -inset-x-[15%] top-[22%] h-[34%]'>
                            <div className={cn('absolute inset-0 bg-[radial-gradient(50%_50%_at_30%_50%,rgba(244,241,230,0.65),transparent),radial-gradient(45%_45%_at_75%_55%,rgba(244,241,230,0.5),transparent)] transition-opacity duration-[1400ms]', night ? 'opacity-0' : 'opacity-100')} />
                            <div className={cn('absolute inset-0 bg-[radial-gradient(50%_50%_at_30%_50%,rgba(70,84,98,0.45),transparent),radial-gradient(45%_45%_at_75%_55%,rgba(70,84,98,0.35),transparent)] transition-opacity duration-[1400ms]', night ? 'opacity-100' : 'opacity-0')} />
                        </div>

                        {ORDER.map((key) => (
                            <motion.div
                                key={`house-${key}`}
                                className='absolute inset-0 transform-gpu will-change-[transform,opacity]'
                                variants={house}
                                initial='below'
                                animate={scene === key ? 'shown' : 'hidden'}
                            >
                                <div className='hero-house-mask absolute bottom-[16%] left-1/2 aspect-video w-[150%] -translate-x-1/2 sm:bottom-[12%] sm:w-[125%] md:w-[110%] lg:inset-0 lg:aspect-auto lg:w-full lg:translate-x-0'>
                                    <Image
                                        src={SCENES[key].image}
                                        alt=''
                                        fill
                                        loading='eager'
                                        fetchPriority={key === 'morning' ? 'high' : 'auto'}
                                        sizes='(min-width: 1024px) 100vw, 150vw'
                                        className='object-cover object-[50%_78%]'
                                    />
                                </div>
                            </motion.div>
                        ))}

                        <div className='hero-fog absolute -inset-x-[15%] bottom-[2%] h-[36%]'>
                            <div className={cn('absolute inset-0 bg-[radial-gradient(45%_55%_at_25%_60%,rgba(238,236,224,0.8),transparent),radial-gradient(40%_50%_at_70%_70%,rgba(238,236,224,0.7),transparent)] transition-opacity duration-[1400ms]', night ? 'opacity-0' : 'opacity-100')} />
                            <div className={cn('absolute inset-0 bg-[radial-gradient(45%_55%_at_25%_60%,rgba(52,64,76,0.6),transparent),radial-gradient(40%_50%_at_70%_70%,rgba(52,64,76,0.5),transparent)] transition-opacity duration-[1400ms]', night ? 'opacity-100' : 'opacity-0')} />
                        </div>

                        <div className={cn('absolute inset-0 transition-opacity duration-[1400ms]', night ? 'opacity-0' : 'opacity-100')}>
                            <div className='absolute inset-x-0 top-0 h-[42%] bg-gradient-to-b from-[#e9e7db]/80 via-[#e9e7db]/30 to-transparent' />
                            <div className='absolute inset-x-0 bottom-0 h-[34%] bg-gradient-to-t from-[#e4e2d4] via-[#e4e2d4]/55 to-transparent' />
                        </div>

                        <div className={cn('absolute inset-0 transition-opacity duration-[1400ms]', night ? 'opacity-100' : 'opacity-0')}>
                            <div className='absolute inset-x-0 top-0 h-[42%] bg-gradient-to-b from-[#121920]/75 via-[#121920]/25 to-transparent' />
                            <div className='absolute inset-x-0 bottom-0 h-[34%] bg-gradient-to-t from-[#151c23] via-[#151c23]/60 to-transparent' />
                        </div>
                    </motion.div>

                    {!still && <motion.div className='absolute inset-0 bg-forest/40' style={{ opacity: lineOpacity }} />}
                </motion.div>

                <div className='relative mx-auto flex h-full max-w-7xl flex-col items-center justify-between px-4 pb-8 pt-28 text-center sm:px-6 sm:pb-10 sm:pt-32 lg:px-8'>
                    <motion.div style={still ? undefined : { y: titleY, opacity: contentOpacity }}>
                        <h1
                            id='hero-title'
                            className='font-heading text-[2.9rem] font-normal leading-[1.02] tracking-[-0.05em] sm:text-6xl lg:text-[5.25rem]'
                        >
                            <SplitReveal
                                text='Sun by day.'
                                trigger='mount'
                                delay={1.05}
                                className={cn('block transition-colors duration-1000', night ? 'text-white' : 'text-primary-strong')}
                            />

                            <SplitReveal
                                text='Storage by night.'
                                trigger='mount'
                                delay={1.3}
                                className={cn('block transition-colors duration-1000', night ? 'text-leaf' : 'text-primary-strong/60')}
                            />
                        </h1>
                    </motion.div>

                    <motion.div
                        style={still ? undefined : { y: controlsY, opacity: contentOpacity }}
                        className='w-full max-w-md'
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease: EASE, delay: 1.7 }}
                            className='flex flex-col items-center gap-5'
                        >
                            <div
                                role='group'
                                aria-label='Time of day'
                                className={cn(
                                    'grid w-full grid-cols-2 gap-1 rounded-2xl p-1.5 ring-1 backdrop-blur-xl transition-colors duration-700',
                                    night ? 'bg-white/10 ring-white/15' : 'bg-white/35 ring-white/60'
                                )}
                            >
                                {ORDER.map((key) => {
                                    const active = scene === key
                                    const { icon: Icon, label, caption } = SCENES[key]

                                    return (
                                        <button
                                            key={key}
                                            type='button'
                                            aria-pressed={active}
                                            onClick={() => choose(key)}
                                            className='relative min-h-16 rounded-xl px-3 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                        >
                                            {active && (
                                                <motion.span
                                                    layoutId='hero-scene-pill'
                                                    transition={{ type: 'spring', stiffness: 360, damping: 34 }}
                                                    className={cn(
                                                        'absolute inset-0 rounded-xl shadow-[0_14px_30px_-18px_rgba(14,26,18,0.55)]',
                                                        night ? 'bg-[#e8ebdc]/90' : 'bg-white/85'
                                                    )}
                                                />
                                            )}

                                            <span
                                                className={cn(
                                                    'relative flex items-center justify-center gap-2 text-base font-medium transition-colors duration-500',
                                                    active ? 'text-forest' : night ? 'text-white/75' : 'text-forest/65'
                                                )}
                                            >
                                                <Icon className='size-4' aria-hidden='true' />
                                                {label}
                                            </span>

                                            <span
                                                className={cn(
                                                    'relative mt-0.5 block text-xs transition-colors duration-500',
                                                    active ? 'text-forest/60' : night ? 'text-white/50' : 'text-forest/50'
                                                )}
                                            >
                                                {caption}
                                            </span>
                                        </button>
                                    )
                                })}
                            </div>

                            <div aria-live='polite' className='min-h-[3.25rem]'>
                                <AnimatePresence mode='wait' initial={false}>
                                    <motion.p
                                        key={scene}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -8 }}
                                        transition={{ duration: 0.4, ease: EASE }}
                                        className={cn(
                                            'text-sm leading-relaxed transition-colors duration-700 sm:text-[15px]',
                                            night ? 'text-white/75' : 'text-forest/75'
                                        )}
                                    >
                                        {SCENES[scene].description}
                                    </motion.p>
                                </AnimatePresence>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>

                {!still && (
                    <motion.p
                        aria-hidden='true'
                        style={{ opacity: lineOpacity, scale: lineScale, filter: lineBlur }}
                        className='pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 px-6 text-center font-heading text-4xl font-light tracking-[-0.04em] text-white sm:text-6xl lg:text-7xl'
                    >
                        Every megawatt, accounted for.
                    </motion.p>
                )}
            </div>
        </section>
    )
}

export default HeroSection
